import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Save,
  Notifications,
  Security,
  Email,
  Sms,
  Language,
  Schedule,
  Add,
  Delete,
  Edit,
  Close
} from '@mui/icons-material';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function Settings() {
  const [tabValue, setTabValue] = useState(3); // Défaut sur l'onglet Catégories
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    urgentAlerts: true,
    maintenanceReminders: true,
    language: 'fr'
  });

  const [urgentCategories, setUrgentCategories] = useState([
    'Plomberie',
    'Électricité', 
    'Chauffage',
    'Sécurité'
  ]);

  const [normalCategories, setNormalCategories] = useState([
    'Nettoyage',
    'Jardinage',
    'Maintenance',
    'Peinture'
  ]);

  const [editDialog, setEditDialog] = useState({
    open: false,
    type: '',
    categories: [],
    newCategory: ''
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleEditCategories = (type) => {
    setEditDialog({
      open: true,
      type: type,
      categories: type === 'urgent' ? [...urgentCategories] : [...normalCategories],
      newCategory: ''
    });
  };

  const handleAddCategory = () => {
    if (editDialog.newCategory.trim()) {
      setEditDialog({
        ...editDialog,
        categories: [...editDialog.categories, editDialog.newCategory.trim()],
        newCategory: ''
      });
    }
  };

  const handleRemoveCategory = (index) => {
    const newCategories = editDialog.categories.filter((_, i) => i !== index);
    setEditDialog({
      ...editDialog,
      categories: newCategories
    });
  };

  const handleSaveCategories = () => {
    if (editDialog.type === 'urgent') {
      setUrgentCategories(editDialog.categories);
    } else {
      setNormalCategories(editDialog.categories);
    }
    
    setEditDialog({ open: false, type: '', categories: [], newCategory: '' });
    setSnackbar({
      open: true,
      message: 'Catégories mises à jour avec succès',
      severity: 'success'
    });

    // Ici, vous feriez normalement un appel API pour sauvegarder
    // axios.put('/api/settings/categories', { type: editDialog.type, categories: editDialog.categories })
  };

  const handleSaveSettings = () => {
    // Sauvegarde des paramètres généraux
    setSnackbar({
      open: true,
      message: 'Paramètres enregistrés avec succès',
      severity: 'success'
    });
    
    // Appel API pour sauvegarder
    // axios.put('/api/settings', settings)
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom>
        Paramètres
      </Typography>

      <Paper elevation={3}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Général" />
          <Tab label="Notifications" />
          <Tab label="Sécurité" />
          <Tab label="Catégories" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" gutterBottom>
              Paramètres généraux
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nom de l'organisation"
                  defaultValue="Square Com"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email de contact"
                  defaultValue="contact@square-com.re"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Téléphone"
                  defaultValue="0262 12 34 56"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Langue"
                  select
                  value={settings.language}
                  SelectProps={{ native: true }}
                  onChange={(e) => setSettings({...settings, language: e.target.value})}
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Adresse"
                  defaultValue="1 Rue de la République, 97400 Saint-Denis"
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>
              Préférences de notification
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Notifications par email"
                  secondary="Recevoir les notifications par email"
                />
                <ListItemSecondaryAction>
                  <Switch 
                    checked={settings.emailNotifications}
                    onChange={(e) => setSettings({...settings, emailNotifications: e.target.checked})}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Notifications SMS"
                  secondary="Recevoir les notifications par SMS (urgences uniquement)"
                />
                <ListItemSecondaryAction>
                  <Switch 
                    checked={settings.smsNotifications}
                    onChange={(e) => setSettings({...settings, smsNotifications: e.target.checked})}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Notifications push"
                  secondary="Notifications dans l'application"
                />
                <ListItemSecondaryAction>
                  <Switch 
                    checked={settings.pushNotifications}
                    onChange={(e) => setSettings({...settings, pushNotifications: e.target.checked})}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <Divider sx={{ my: 2 }} />
              <ListItem>
                <ListItemText 
                  primary="Alertes urgentes"
                  secondary="Être notifié immédiatement pour les urgences"
                />
                <ListItemSecondaryAction>
                  <Switch 
                    checked={settings.urgentAlerts}
                    onChange={(e) => setSettings({...settings, urgentAlerts: e.target.checked})}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Rappels de maintenance"
                  secondary="Rappels pour les maintenances programmées"
                />
                <ListItemSecondaryAction>
                  <Switch 
                    checked={settings.maintenanceReminders}
                    onChange={(e) => setSettings({...settings, maintenanceReminders: e.target.checked})}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>
              Sécurité
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Changer le mot de passe
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="Mot de passe actuel"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="Nouveau mot de passe"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="Confirmer le nouveau mot de passe"
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained">
                  Changer le mot de passe
                </Button>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" gutterBottom>
              Catégories d'intervention
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom color="error">
                    Catégories urgentes
                  </Typography>
                  <List dense>
                    {urgentCategories.map((cat, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={`• ${cat}`} />
                      </ListItem>
                    ))}
                  </List>
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={() => handleEditCategories('urgent')}
                  >
                    MODIFIER
                  </Button>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom color="primary">
                    Catégories normales
                  </Typography>
                  <List dense>
                    {normalCategories.map((cat, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={`• ${cat}`} />
                      </ListItem>
                    ))}
                  </List>
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={() => handleEditCategories('normal')}
                  >
                    MODIFIER
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>
        </Box>

        <Box sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="contained" 
            startIcon={<Save />}
            onClick={handleSaveSettings}
          >
            ENREGISTRER LES MODIFICATIONS
          </Button>
        </Box>
      </Paper>

      {/* Dialog pour éditer les catégories */}
      <Dialog 
        open={editDialog.open} 
        onClose={() => setEditDialog({ open: false, type: '', categories: [], newCategory: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Modifier les catégories {editDialog.type === 'urgent' ? 'urgentes' : 'normales'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {editDialog.categories.map((category, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Chip 
                  label={category}
                  color={editDialog.type === 'urgent' ? 'error' : 'primary'}
                  sx={{ flex: 1, justifyContent: 'flex-start' }}
                />
                <IconButton 
                  size="small" 
                  onClick={() => handleRemoveCategory(index)}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </Box>
            ))}
            
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Nouvelle catégorie"
                value={editDialog.newCategory}
                onChange={(e) => setEditDialog({...editDialog, newCategory: e.target.value})}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddCategory();
                  }
                }}
              />
              <Button 
                variant="contained" 
                onClick={handleAddCategory}
                startIcon={<Add />}
              >
                Ajouter
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, type: '', categories: [], newCategory: '' })}>
            Annuler
          </Button>
          <Button onClick={handleSaveCategories} variant="contained">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Settings;
